import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CaretLeft, CaretRight } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_FORMAT } from "@/components/ui/date-picker"
import { DateOnly } from "@/lib/date-only"
import { DATE_FORMATS, formatDate } from "@/lib/format-date"
import { getServerDateOnly } from "@/lib/initial-time-zone"
import { cn } from "@/lib/utils"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"

const DATE_SHORTCUTS = [2, 3, 5, 10] as const

type SlotDatePickerProps = {
  hasAvailableSlot?: boolean
  availableDates?: readonly string[]
  isIrrelevant?: boolean
  onChange: (value: DateOnly) => void
}
export const SlotDatePicker = (props: SlotDatePickerProps) => {
  const { availableDates = [], hasAvailableSlot, isIrrelevant, onChange } = props
  const { t } = useTranslation()
  const { setValue, control, getFieldState, resetField } = useFormContext<TeleconsultationForm>()
  const { error } = getFieldState("date")

  const isEnableNextDateButton = availableDates.length === 0 && !isIrrelevant
  const today = getServerDateOnly()

  const isDateInRange = (date: DateOnly) => date.isWithinIncludingRange(today, today.addMonths(3))

  const handleSelect = (date: Date) => {
    const selectedDate = new DateOnly(date)
    setValue("date", selectedDate, { shouldDirty: true })
    onChange(selectedDate)
  }

  const handleReset = () => {
    resetField("date")
  }

  const updateDate = (date: DateOnly, shouldReset = false) => {
    const isAllowDate = isDateInRange(date)
    let latest: DateOnly | undefined = undefined

    if (isAllowDate) latest = date
    if (!isAllowDate && shouldReset) latest = today

    if (latest) {
      setValue("date", latest, { shouldDirty: true })
      onChange(latest)
    }
  }

  const handleShortcut = (days: number) => {
    updateDate(today.addDays(days))
  }

  const onHandleNextAvailableClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    if (isEnableNextDateButton) return

    const selectedDate = event.currentTarget.getAttribute("data-date")

    if (!selectedDate) {
      return updateDate(new DateOnly(availableDates[0]))
    }

    if (isIrrelevant) {
      return updateDate(new DateOnly(selectedDate).addDays(1), true)
    }

    const nextIndex = availableDates.findIndex((d) => new DateOnly(d).isGreaterThan(new DateOnly(selectedDate)))
    return updateDate(new DateOnly(availableDates[nextIndex === -1 ? 0 : nextIndex]))
  }

  return (
    <div className="flex flex-col gap-3 p-0.5" data-testid="patient-teleconsultation-date-picker">
      <div className="flex items-center gap-4">
        <Controller
          name="date"
          control={control}
          render={({ field: { value } }) => {
            const isPreviousDisabled = !hasAvailableSlot || !isDateInRange(value.addDays(-1))
            const isNextDisabled = !hasAvailableSlot || !isDateInRange(value.addDays(1))
            return (
              <>
                <DatePicker
                  disabledMatcher={(d) => !isDateInRange(new DateOnly(d))}
                  readOnly={false}
                  data-testid="selected-date"
                  placeholder={VISUAL_DATE_FORMAT}
                  value={value.toDate()}
                  today={today.toDate()}
                  className="h-12"
                  onSelect={handleSelect}
                  onReset={handleReset}
                  error={error?.message}
                  wrapperClassName={cn("w-57.5", { "cursor-not-allowed": !hasAvailableSlot })}
                  errorClasses="truncate"
                  translateError
                  disabled={!hasAvailableSlot}
                />
                <div className="flex items-center gap-2">
                  <Button
                    variant="tertiary"
                    className="p-2"
                    data-testid="left-arrow-button"
                    onClick={() => updateDate(value.addDays(-1))}
                    disabled={isPreviousDisabled}>
                    <CaretLeft
                      className={cn("h-6 w-6", isPreviousDisabled ? "text-on-surface-disabled" : "text-on-surface")}
                    />
                  </Button>
                  <Button
                    variant="tertiary"
                    className="p-2"
                    data-testid="right-arrow-button"
                    onClick={() => updateDate(value.addDays(1))}
                    disabled={isNextDisabled}>
                    <CaretRight
                      className={cn("h-6 w-6", isNextDisabled ? "text-on-surface-disabled" : "text-on-surface")}
                    />
                  </Button>
                </div>
                <Button
                  variant="primary"
                  data-testid="get-next-date-button"
                  data-date={formatDate(value.toDate(), DATE_FORMATS.ISODateFormat)}
                  onClick={onHandleNextAvailableClick}
                  disabled={isEnableNextDateButton}>
                  {t("patient.agenda.getNextAvailableDate")}
                </Button>
              </>
            )
          }}
        />
      </div>
      <div className="flex items-center gap-2" data-testid="date-shortcut-buttons">
        {DATE_SHORTCUTS.map((days) => (
          <Button
            key={days}
            type="button"
            variant="secondary"
            className="px-3 py-1.5 text-sm"
            data-testid={`date-shortcut-${days}`}
            onClick={() => handleShortcut(days)}
            disabled={!hasAvailableSlot}>
            {t("patient.agenda.dateShortcut", { days })}
          </Button>
        ))}
      </div>
    </div>
  )
}
